import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Table } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ColumnVisibilityProps<TData> {
  table: Table<TData>;
}

// Map column IDs to their translation keys for special cases
const getColumnTranslationKey = (columnId: string): string => {
  switch (columnId) {
    case 'status':
      return 'status.status';
    case 'actions':
      return 'actionsLabel';
    case 'result':
      return 'result.result';
    case 'is_active':
      return 'isActive';
    case 'document':
      return 'inn';
    default:
      return columnId;
  }
};

export function ColumnVisibility<TData>({ table }: ColumnVisibilityProps<TData>) {
  const { t } = useTranslation();

  const hidableColumns = table.getAllColumns().filter((col) => col.getCanHide());

  if (hidableColumns.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          {t('columns')} <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* Cap to the viewport space Radix reports so long column lists scroll
          instead of overflowing the screen. */}
      <DropdownMenuContent
        align="end"
        className="max-h-[min(60vh,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto"
      >
        {hidableColumns.map((col) => (
          <DropdownMenuCheckboxItem
            key={col.id}
            className="capitalize"
            checked={col.getIsVisible()}
            onCheckedChange={(val) => col.toggleVisibility(!!val)}
          >
            {t(getColumnTranslationKey(col.id))}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
